import { createFileRoute, type ErrorComponentProps } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'

import { hasCode } from '@/api/errors'
import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import EmptyState from '@/components/ui/EmptyState'
import NotAvailable from '@/components/ui/NotAvailable'
import Pagination from '@/components/ui/Pagination'
import SearchInput from '@/components/ui/SearchInput'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import { BROKER_LIST_SORTS_EXTRA_AVAILABLE } from '@/features/brokers/api/brokerListSorts'
import { useBrokerList } from '@/features/brokers/api/queries'
import { useOnline } from '@/hooks/useOnline'
import { resolveViewState } from '@/lib/view-state'
import type { PageSize } from '@/schemas/table'
import type { BrokerListSort } from '@/schemas/broker'
import BrokerRankList from '@/screens/brokers/components/all-brokers/BrokerRankList'
import SortMenu, { type AllBrokersSort } from '@/screens/brokers/components/all-brokers/SortMenu'
import TmsChip from '@/screens/brokers/components/all-brokers/TmsChip'
import { loadAllBrokers } from '@/screens/brokers/AllBrokersScreen.loader'
import { allBrokersSearchSchema, toBrokerListQuery } from '@/screens/brokers/search'

const HEADER = <TitleRow title="All Brokers" />

/**
 * `brokerListQuerySchema`'s own `pageSize` default (20, the mock's) is the row count — a
 * shorter search result page is narrower than this, never taller. Rows are the
 * two-line `lg` height the loaded table's `rowHeight="lg"` draws (a mark beside
 * a name over its code); five columns, the Broker one and four figures.
 */
const SKELETON_ROWS = 20

function AllBrokersPending() {
  return (
    <ScreenLayout pending pendingLabel="Loading brokers…" header={HEADER} pageTitle="All Brokers">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 @md:flex-row @md:items-center @md:justify-between">
          <Skeleton className="h-11 w-full rounded-md lg:h-9 @md:w-52" />
          <Skeleton className="h-11 w-32 rounded-md lg:h-9" />
        </div>
        <div>
          <DataTableSkeleton rows={SKELETON_ROWS} columns={5} rowHeight="lg" />
          {/* `Pagination`'s own row, 24 under the table: rows-per-page on the left, pages right. */}
          <div className="mt-6 flex h-8 items-center justify-between gap-3">
            <Skeleton className="h-4 w-32 rounded-xs" />
            <Skeleton className="h-8 w-56 rounded-sm" />
          </div>
        </div>
      </div>
      <Skeleton className="h-4 w-full max-w-2xl rounded-xs" />
    </ScreenLayout>
  )
}

/**
 * Reuses `listFailureMessage` below rather than a generic RouteErrorPage
 * copy: `AllBrokersScreen.test.tsx` pins the exact per-error-code sentence,
 * and a first-load failure of this screen's one query is now this route's
 * error, not the screen's own `view === 'failed'` branch (that branch still
 * fires for a search/sort/page change that fails after the page has mounted
 * — see docs/route-loading.md, "Search params").
 */
function AllBrokersError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="Couldn't load brokers"
      description={listFailureMessage(props.error)}
    />
  )
}

export const Route = createFileRoute('/_app/brokers/all')({
  validateSearch: allBrokersSearchSchema,
  loader: loadAllBrokers,
  pendingComponent: AllBrokersPending,
  errorComponent: AllBrokersError,
  component: AllBrokersScreen,
})

/** The ordering the table is in, for its accessible name. */
const SORT_CAPTION: Record<BrokerListSort, string> = {
  turnover: 'sorted by turnover',
  net: 'sorted by net flow',
  name: 'sorted alphabetically by name',
}

/**
 * Why a list request failed, in terms this screen can act on. Branching on
 * `error.code` through `hasCode`, never on `error.message` — the same rule
 * every other broker screen in this feature follows (see e.g.
 * `BrokerScreen.tsx`'s `notFound` check).
 */
const listFailureMessage = (error: unknown): string =>
  hasCode(error, 'VALIDATION_ERROR')
    ? 'That search or sort combination isn’t valid. Try clearing the search box.'
    : 'Try refreshing the page.'

/**
 * Brokers · All brokers (Figma `2333:16764`, state `3047:27901` "Sort open").
 *
 * `Route.useSearch()` is `BrokerListQuery`-shaped (`allBrokersSearchSchema`
 * re-uses `brokerListQuerySchema` by reference, per `search.ts`), but is not
 * handed to `useBrokerList` as-is — `toBrokerListQuery` picks the known
 * fields first, so a stray URL param TanStack Router's search merge left on
 * `Route.useSearch()` cannot split the cache key the loader already warmed.
 *
 * Pages revamp (2026-10-27): the section strip names the page, so the screen
 * opens on its toolbar, then ONE table — Broker · Turnover today · Buy amount ·
 * Sell amount · Rating — see `BrokerRankList` for why that is one ordered list.
 * The "Licensed brokers" head and its member count are gone with the mock's, and
 * "How TMS works" with them: its explainer sheet never shipped, so the chip was
 * an inert stand-in.
 */
function AllBrokersScreen() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const query = useBrokerList(toBrokerListQuery(search))
  const online = useOnline()
  // `data` on screen wins over a failed background refetch — see
  // `resolveViewState`'s own doc comment. `useBrokerList` also carries
  // `keepPreviousData`, which this now shares with `BrokerRankList`'s
  // `isLoading` prop below rather than only the raw `isPending`.
  const view = resolveViewState(query, { online })

  const [term, setTerm] = useState(search.q ?? '')
  const [sortOpen, setSortOpen] = useState(false)
  // "Shares traded" / "Broker number": in the menu like the mock's, but nothing serves them. They
  // live here rather than in the URL, so a link never carries an order the API would refuse.
  const [unservedSort, setUnservedSort] = useState<'shares' | 'code' | null>(null)
  const sort: AllBrokersSort = unservedSort ?? search.sort

  // Hand-rolled debounce, matching `StockPricesScreen`'s own: 300ms after the
  // last keystroke the term lands in the URL as `q` and the page resets to 1.
  //
  // Guarded by comparing against the last `q` actually committed to the URL
  // (see CompaniesScreen.tsx's own comment on this pattern), the same fix as
  // StockPricesScreen: unconditionally, this effect's mount run fired
  // `navigate` once on every load and smuggled an unprompted `page: 1` into
  // the URL of a page nobody had typed into yet.
  const lastCommittedQuery = useRef(search.q)
  useEffect(() => {
    const next = term.trim() === '' ? undefined : term
    if (next === lastCommittedQuery.current) return
    const id = setTimeout(() => {
      lastCommittedQuery.current = next
      void navigate({
        search: (prev) => ({ ...prev, q: next, page: 1 }),
      })
    }, 300)
    return () => {
      clearTimeout(id)
    }
  }, [term, navigate])

  const data = query.data

  return (
    <ScreenLayout header={HEADER} pageTitle="All Brokers">
      {/* Toolbar and table sit 16 apart (the pages revamp's one rhythm); the
          pager keeps its own 24 under the table. */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 @md:flex-row @md:items-center @md:justify-between">
          <SearchInput
            aria-label="Broker number or name"
            placeholder="Broker number or name"
            value={term}
            onChange={(event) => {
              setTerm(event.target.value)
            }}
            onClear={() => {
              setTerm('')
            }}
            className="@md:w-52"
          />
          <SortMenu
            value={sort}
            onChange={(next) => {
              if (next === 'shares' || next === 'code') {
                setUnservedSort(next)
                return
              }
              setUnservedSort(null)
              void navigate({ search: (prev) => ({ ...prev, sort: next, page: 1 }) })
            }}
            open={sortOpen}
            onOpenChange={setSortOpen}
          />
        </div>

        {unservedSort !== null && !BROKER_LIST_SORTS_EXTRA_AVAILABLE ? (
          <NotAvailable
            thing={
              unservedSort === 'shares'
                ? 'Sorting brokers by shares traded'
                : 'Sorting brokers by broker number'
            }
          />
        ) : view === 'failed' || view === 'offline' ? (
          <EmptyState
            kind={view}
            size="page"
            onRetry={() => void query.refetch()}
            retrying={query.isFetching}
            savedAt={query.dataUpdatedAt}
            {...(view === 'failed'
              ? { title: "Couldn't load brokers", description: listFailureMessage(query.error) }
              : {})}
          />
        ) : (
          // A <div>, not a fragment: a fragment's children are children of the
          // flex stack, so its `gap-4` fell between the table and its pager and
          // would now add to `Pagination`'s own 24px.
          <div>
            <BrokerRankList
              rows={data?.rows ?? []}
              label={`Licensed brokers, ${SORT_CAPTION[search.sort]}`}
              isLoading={view === 'loading'}
              skeletonRows={search.pageSize}
              emptyState={
                search.q === undefined ? (
                  <EmptyState kind="empty" title="No brokers to show" />
                ) : (
                  // The mock's sentence, centred, with no icon block. The head stays.
                  <p className="px-4 py-8 text-center text-body-m text-muted-foreground">
                    No broker matches “{search.q}”. Try a number like 58 or a name like Nabil.
                  </p>
                )
              }
            />

            <Pagination
              page={data?.page ?? search.page}
              pageSize={data?.pageSize ?? search.pageSize}
              total={data?.total ?? 0}
              unit="brokers"
              onPageChange={(page) => {
                void navigate({ search: (prev) => ({ ...prev, page }) })
              }}
              onPageSizeChange={(pageSize) => {
                void navigate({
                  search: (prev) => ({ ...prev, pageSize: pageSize as PageSize, page: 1 }),
                })
              }}
            />
            {/* The mock's chip: 12 under the pager, 20 above the page's foot. */}
            <div className="mt-3 pb-5">
              <TmsChip />
            </div>
          </div>
        )}
      </div>
    </ScreenLayout>
  )
}
